import { fetchWithTimeout, retry } from "@layerswap/widget/internal";
const parseBalanceAmount = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
};
export const getAvailableTokenBalance = (state, coin) => {
    const balance = state.balances.find(b => b.coin.toUpperCase() === coin.toUpperCase());
    const total = parseBalanceAmount(balance?.total);
    const hold = parseBalanceAmount(balance?.hold);
    const available = Math.max(total - hold, 0);
    return {
        coin,
        total,
        hold,
        available,
    };
};
export class HyperliquidClient {
    async getClearinghouseState(user, nodeUrl, timeoutMs, retryCount) {
        const response = await retry(async () => await fetchWithTimeout(`${nodeUrl}/info`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'clearinghouseState',
                user: user,
            }),
            timeoutMs: timeoutMs ?? 60000,
        }), retryCount ?? 3, 500);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    }
    async getSpotClearinghouseState(user, nodeUrl, timeoutMs, retryCount) {
        const response = await retry(async () => await fetchWithTimeout(`${nodeUrl}/info`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'spotClearinghouseState',
                user: user,
            }),
            timeoutMs: timeoutMs ?? 60000,
        }), retryCount ?? 3, 500);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    }
    /**
     * Free, withdrawable USDC split across both HyperCore pools. The `sendToEvmWithData`
     * withdrawal can pull from either (spot or perp) via `sourceDex`, so the caller needs
     * the split to pick a pool — or consolidate via `usdClassTransfer` when neither alone
     * covers the amount. Fetches both clearinghouse states concurrently; either failing
     * rejects the whole split (we can't decide a source on partial data).
     */
    async getWithdrawableSplit(user, nodeUrl, coin, timeoutMs, retryCount) {
        const [spotState, perpsState] = await Promise.all([
            this.getSpotClearinghouseState(user, nodeUrl, timeoutMs, retryCount),
            this.getClearinghouseState(user, nodeUrl, timeoutMs, retryCount),
        ]);
        const spot = getAvailableTokenBalance(spotState, coin).available;
        // `withdrawable` is the free USDC after margin — the correct field, not
        // accountValue/totalRawUsd (which include margin-locked equity).
        const perps = Math.max(parseBalanceAmount(perpsState.withdrawable), 0);
        return { spot, perps, combined: spot + perps };
    }
    /**
     * Submit a signed `sendToEvmWithData` action to Hyperliquid's exchange endpoint.
     * Deliberately NOT wrapped in `retry`: the action carries a time-bound nonce
     * and is signed, so a blind retry risks a double-submit.
     */
    async withdraw(action, signature, nodeUrl, timeoutMs) {
        const response = await fetchWithTimeout(`${nodeUrl}/exchange`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action, nonce: action.nonce, signature }),
            timeoutMs: timeoutMs ?? 60000,
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    }
    /**
     * Submit a signed `usdClassTransfer` action to move USDC between the user's
     * perp and spot pools. Like `withdraw`, deliberately NOT wrapped in `retry`:
     * the action carries a time-bound nonce and is signed, so a blind retry risks
     * a double-submit.
     */
    async usdClassTransfer(action, signature, nodeUrl, timeoutMs) {
        const response = await fetchWithTimeout(`${nodeUrl}/exchange`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action, nonce: action.nonce, signature }),
            timeoutMs: timeoutMs ?? 60000,
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    }
}
